import React, { useContext } from 'react';
import { AbilityContext } from '../../../../components/ability/can';
import { CollapseFilterWrapper } from '../../../../components/sidebar-filters/CollapseFilterWrapper';
import { CaisoResourceFilterSelect } from '../../../../components/sidebar-filters/filter-select/CaisoResourceFilterSelect';
import { ClosedSiteFilterSelect } from '../../../../components/sidebar-filters/filter-select/ClosedSiteFilterSelect';
import { ControlProviderFilterSelect } from '../../../../components/sidebar-filters/filter-select/ControlProviderFilterSelect';
import { DispatchTypeFilterSelect } from '../../../../components/sidebar-filters/filter-select/DispatchTypeFilterSelect';
import { HasVenFilterSelect } from '../../../../components/sidebar-filters/filter-select/HasVenFilterSelect';
import { IntervalDataConnectionFilterSelect } from '../../../../components/sidebar-filters/filter-select/IntervalDataConnectionFilterSelect';
import { MarketFilterSelect } from '../../../../components/sidebar-filters/filter-select/MarketFilterSelect';
import { MeterEnrollmentFilterSelect } from '../../../../components/sidebar-filters/filter-select/MeterEnrollmentFilterSelect';
import { MeterFilterSelect } from '../../../../components/sidebar-filters/filter-select/MeterFilterSelect';
import { ProgramFilterSelect } from '../../../../components/sidebar-filters/filter-select/ProgramFilterSelect';
import { SiteStatusFilterSelect } from '../../../../components/sidebar-filters/filter-select/SiteStatusFilterSelect';
import { UtilityCustomerFilterSelect } from '../../../../components/sidebar-filters/filter-select/UtilityCustomerFilterSelect';
import { UtilityFilterSelect } from '../../../../components/sidebar-filters/filter-select/UtilityFilterSelect';
import { useAuth } from '../../../../domain/auth/useAuth';
import { SiteFilterKeys, SiteFilterLabels } from '../../../../domain/saved-filters/interface';
import { useEnergyUsageSidebarFiltersContext } from '../context/EnergyUsageSidebarFiltersContext';
import { useSidebarFilterOptionsContext } from '../context/SidebarFilterOptionsContext';

interface IEnergyUsageSidebarFiltersProps {}

// TODO: Investigate if need to fix the height calculation to be more dynamic if needed
const HEIGHT = 'calc(100vh - 160px)'; // viewport height - page header - button bar height

export const EnergyUsageSidebarFilters: React.FC<IEnergyUsageSidebarFiltersProps> = () => {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                height: HEIGHT,
            }}
        >
            <FiltersContent />
        </div>
    );
};

const FiltersContent: React.FC = () => {
    const user = useAuth()!.user!;
    const ability = useContext(AbilityContext);
    const isAdminRole = user.isAdminRoleType();
    const isControlProviderRole = user.isControlProvider();

    const { siteFilters, handleSiteFilterChange } = useEnergyUsageSidebarFiltersContext();
    const {
        utilityOptions,
        controlProviderOptions,
        isLoading: isFiltersLoading,
        marketOptions,
        programOptions,
        siteStatusOptions,
        dispatchTypeOptions,
        meterEnrollmentOptions,
        meterOptions,
        hasVenOptions,
        utilityCustomerOptions,
        siteIntervalDataOptions,
        closedSitesOptions,
        caisoResourceOptions,
    } = useSidebarFilterOptionsContext();

    const filtersItems = [
        ability.can('read', 'ControlProvider') && (
            <ControlProviderFilterSelect
                key={SiteFilterKeys.controlProviderId}
                label={SiteFilterLabels[SiteFilterKeys.controlProviderId]}
                value={siteFilters[SiteFilterKeys.controlProviderId] || []}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.controlProviderId, value)}
                options={controlProviderOptions}
                isLoading={isFiltersLoading}
            />
        ),
        ability.can('read', 'UtilityCustomer') && (
            <UtilityCustomerFilterSelect
                key={SiteFilterKeys.utilityCustomerId}
                label={SiteFilterLabels[SiteFilterKeys.utilityCustomerId]}
                value={siteFilters[SiteFilterKeys.utilityCustomerId] || []}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.utilityCustomerId, value)}
                options={utilityCustomerOptions}
                isLoading={isFiltersLoading}
            />
        ),
        <MarketFilterSelect
            key={SiteFilterKeys.market}
            label={SiteFilterLabels[SiteFilterKeys.market]}
            value={siteFilters[SiteFilterKeys.market] || []}
            onChange={value => handleSiteFilterChange(SiteFilterKeys.market, value)}
            options={marketOptions}
            isLoading={isFiltersLoading}
        />,
        <ProgramFilterSelect
            key={SiteFilterKeys.programId}
            label={SiteFilterLabels[SiteFilterKeys.programId]}
            value={siteFilters[SiteFilterKeys.programId] || []}
            onChange={value => handleSiteFilterChange(SiteFilterKeys.programId, value)}
            options={programOptions}
            isLoading={isFiltersLoading}
        />,
        isAdminRole && (
            <SiteStatusFilterSelect
                key={SiteFilterKeys.status}
                label={SiteFilterLabels[SiteFilterKeys.status]}
                value={siteFilters[SiteFilterKeys.status] || []}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.status, value)}
                options={siteStatusOptions}
            />
        ),
        <UtilityFilterSelect
            key={SiteFilterKeys.utility}
            label={SiteFilterLabels[SiteFilterKeys.utility]}
            value={siteFilters[SiteFilterKeys.utility] || []}
            onChange={value => handleSiteFilterChange(SiteFilterKeys.utility, value)}
            options={utilityOptions}
            isLoading={isFiltersLoading}
        />,
        isAdminRole && (
            <CaisoResourceFilterSelect
                key={SiteFilterKeys.caisoResource}
                label={SiteFilterLabels[SiteFilterKeys.caisoResource]}
                value={siteFilters[SiteFilterKeys.caisoResource] || []}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.caisoResource, value)}
                isLoading={isFiltersLoading}
                options={caisoResourceOptions}
            />
        ),
    ];

    const optionsItems = [
        ability.can('close-open', 'Site') && (
            <ClosedSiteFilterSelect
                key={SiteFilterKeys.isClosed}
                label={SiteFilterLabels[SiteFilterKeys.isClosed]}
                value={siteFilters[SiteFilterKeys.isClosed] || null}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.isClosed, value)}
                options={closedSitesOptions}
            />
        ),
        <DispatchTypeFilterSelect
            key={SiteFilterKeys.dispatchType}
            label={SiteFilterLabels[SiteFilterKeys.dispatchType]}
            value={siteFilters[SiteFilterKeys.dispatchType] || null}
            onChange={value => handleSiteFilterChange(SiteFilterKeys.dispatchType, value)}
            options={dispatchTypeOptions}
        />,
        isAdminRole && (
            <HasVenFilterSelect
                key={SiteFilterKeys.hasVen}
                label={SiteFilterLabels[SiteFilterKeys.hasVen]}
                value={siteFilters[SiteFilterKeys.hasVen] || null}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.hasVen, value)}
                options={hasVenOptions}
            />
        ),
        (isAdminRole || isControlProviderRole) && (
            <IntervalDataConnectionFilterSelect
                key={SiteFilterKeys.intervalData}
                label={SiteFilterLabels[SiteFilterKeys.intervalData]}
                value={siteFilters[SiteFilterKeys.intervalData] || null}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.intervalData, value)}
                options={siteIntervalDataOptions}
            />
        ),
        isAdminRole && (
            <MeterEnrollmentFilterSelect
                key={SiteFilterKeys.existSalesforceMeterEnrollments}
                label={SiteFilterLabels[SiteFilterKeys.existSalesforceMeterEnrollments]}
                value={siteFilters[SiteFilterKeys.existSalesforceMeterEnrollments] || null}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.existSalesforceMeterEnrollments, value)}
                options={meterEnrollmentOptions}
            />
        ),
        isAdminRole && (
            <MeterFilterSelect
                key={SiteFilterKeys.existSalesforceMeters}
                label={SiteFilterLabels[SiteFilterKeys.existSalesforceMeters]}
                value={siteFilters[SiteFilterKeys.existSalesforceMeters] || null}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.existSalesforceMeters, value)}
                options={meterOptions}
            />
        ),
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', overflowY: 'auto' }}>
            {/* Filters */}
            <CollapseFilterWrapper label="Filters">{filtersItems}</CollapseFilterWrapper>

            {/* Options */}
            {optionsItems.length > 0 && <CollapseFilterWrapper label="Options">{optionsItems}</CollapseFilterWrapper>}
        </div>
    );
};
